import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import memoize from 'memoize-one';
import isEqual from 'lodash/isEqual';
import { styled } from '@compiled/react';
import gql from 'graphql-tag';

import { EmojiResource } from '@atlaskit/emoji/resource';
import { insm } from '@atlaskit/insm';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { EditorActions } from '@atlaskit/editor-core';
import type { InlineCommentPluginState } from '@atlaskit/editor-plugin-annotation';
import type { ExtensionManifest } from '@atlaskit/editor-common/extensions';
import { DefaultExtensionProvider } from '@atlaskit/editor-common/extensions';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { ExtensionPlugin } from '@atlaskit/editor-plugins/extension';
import {
	getConfluencePageAri,
	useMemoizedBlockServiceAPIProviders,
	useMemoizedContentAPIProviders,
	useMemoizedSyncedBlockProvider,
} from '@atlaskit/editor-synced-block-provider';
import UFOLabel from '@atlaskit/react-ufo/label';

import { EditorForgeExtensionConfigHelperRenderer } from '@atlassian/xen-editor-provider';

import { isMac } from '@confluence/shortcuts/entry-points/ShortcutVisualizer';
import { useCommentsPanelScroll } from '@confluence/comments-panel-utils/entry-points/useCommentsPanelScroll';
import {
	useCommentsPanelActions,
	ViewValues,
} from '@confluence/comments-panel-utils/entry-points/useCommentsPanel';
import { useShowCommentsPanel } from '@confluence/comments-panel/entry-points/useShowCommentsPanel';
import { EditorCommentShortcutListener } from '@confluence/comments-shortcuts/entry-points/EditorCommentShortcutListener';
import { ObjectCommentProvider } from '@confluence/comment-context/entry-points/ObjectCommentContext';
import {
	InlineCommentsProvider,
	useInlineCommentsDispatchContext,
} from '@confluence/comment-context/entry-points/InlineCommentsContext';
import { ReactionsProvider } from '@confluence/comment-context/entry-points/ReactionsContext';
import { EndOfPageRecommendationFinalLoader } from '@confluence/page-recommendations/entry-points/EndOfPageRecommendationLoader';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { fg } from '@confluence/feature-gating';
import { PageCommentsPubSubHandler } from '@confluence/comments-pubsub/entry-points/pageComments';
import { useIsMentionOnRestrictedPageEligible } from '@confluence/experiment-mention-on-restricted-page';
import { expValEquals, expVal } from '@confluence/feature-experiments';
import { useObjectSidebarActions } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar';
import type { LoadingPriority } from '@confluence/loadable';
import { useIsDesktop } from '@confluence/desktop-native-bridge';
import { useCommentsData } from '@confluence/comments-data/entry-points/useCommentsData';
import { useEditorContentModeValue } from '@confluence/editor-content-mode-button/entry-points/useEditorContentModeValue';
import { MovePageSuccessFlag } from '@confluence/page-tree/entry-points/MovePageSuccessFlag';
import { CommentsDataBackgroundLoader } from '@confluence/comments-data-manager/entry-points/CommentsDataBackgroundLoader';

// The DO_NOT_USE here is an artifact of an old implementation. We can, in fact, use this

import { SmartButtonConfigPanel as EditorContextSmartButtonConfigPanel } from '@confluence/automation-extensions/entry-points/SmartButtonConfigPanel';
import { useSmartButtonPremiumGate } from '@confluence/automation-extensions/entry-points/useSmartButtonPremiumGate';
import { useSmartButtonConfigPanel } from '@confluence/automation-extensions/entry-points/useSmartButtonConfigPanel';
import { QuickReload } from '@confluence/quick-reload/entry-points/QuickReload';
import { PageMode } from '@confluence/quick-reload/entry-points/pubSubConstants';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import { waitForGlobal } from '@confluence/wrm/entry-points/waitForGlobal';
import {
	VIEW_ALL_MACRO_EXPERIENCE,
	VIEW_INLINE_COMMENT_EXPERIENCE,
	OBJECT_HEADER_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName';
import {
	ExperienceTrackerContext,
	ExperienceStart,
} from '@confluence/experience-tracker/entry-points/component';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout';
import { useRetrieveEditorTitleAndContent } from '@confluence/editor-features/entry-points/useRetrieveEditorTitleAndContent';
import { EditorAnalyticsListener } from '@confluence/fabric-analytics-listeners/entry-points/EditorAnalyticsListener';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import { useExposeGoogleMeetScreenShare } from '@confluence/embedded-google-meet/entry-points/useExposeGoogleMeetScreenShare';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import type { WithDialogsProps } from '@confluence/dialogs/entry-points/withDialogs';
import { withDialogs } from '@confluence/dialogs/entry-points/withDialogs';
import {
	TriggerSaveIndicatorContext,
	SaveIndicatorContextProvider,
} from '@confluence/save-indicator/entry-points/SaveIndicatorContext';
// Metrics and Instrumentation related
import { PageLoadMark } from '@confluence/browser-metrics/entry-points/PageLoadMark';
import { StickyDraftCommentContainer } from '@confluence/object-comments/entry-points/StickyDraftCommentContainer';
import { ObjectCommentWrapper } from '@confluence/object-comments/entry-points/ObjectCommentWrapper';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
// Editor common functionalities and providers
import { useGetChannelVariables } from '@confluence/editor-features/entry-points/useGetChannelVariables';
import { type WithDocChangeContextProps } from '@confluence/editor-features/entry-points/WithDocChangeContextProps';
import { type ProseMirrorRenderedDataType } from '@confluence/editor-features/entry-points/ProseMirrorRenderedDataType';
import { useActivityProvider } from '@confluence/editor-features/entry-points/useActivityProvider';
import { useAutoformattingProvider } from '@confluence/editor-features/entry-points/useAutoformattingProvider';
import { useContextIdentifierProvider } from '@confluence/editor-features/entry-points/useContextIdentifierProvider';
import { useDisabledAnalytics } from '@confluence/editor-features/entry-points/useDisabledAnalytics';
import { useDraftSyncAndPublish } from '@confluence/editor-features/entry-points/useDraftSyncAndPublish';
import { useEditorAnalyticsEvent } from '@confluence/editor-features/entry-points/useEditorAnalyticsEvent';
import { useEditorFeatureFlags } from '@confluence/editor-features/entry-points/useEditorFeatureFlags';
import { useEditorTitleFocus } from '@confluence/editor-features/entry-points/useEditorTitleFocus';
import { getFindReplaceOptions } from '@confluence/editor-features/entry-points/getFindReplaceOptions';
import { useHandleCollabError } from '@confluence/editor-features/entry-points/useHandleCollabError';
import { useInjectDraftContent } from '@confluence/editor-features/entry-points/useInjectDraftContent';
import { useMediaOptions } from '@confluence/editor-features/entry-points/useMediaOptions';
import { useMentionProvider } from '@confluence/editor-features/entry-points/useMentionProvider';
import { useSendContentAgeAnalytics } from '@confluence/editor-features/entry-points/useSendContentAgeAnalytics';
import { useSearchProvider } from '@confluence/editor-features/entry-points/useSearchProvider';
import { useSpinner, Spinner } from '@confluence/editor-features/entry-points/useSpinner';
import { useEditorPluginAIOptions } from '@confluence/editor-features/entry-points/useEditorPluginAIOptions';
import { useEditorPluginAIFeedbackHandlers } from '@confluence/editor-features/entry-points/useEditorPluginAIFeedbackHandlers';
import { useEditorPluginAICollabConnector } from '@confluence/editor-features/entry-points/useEditorPluginAICollabConnector';
import { useDocumentActive } from '@confluence/editor-features/entry-points/useDocumentActive';
import { useEditorInlineCommentsPubSub } from '@confluence/editor-features/entry-points/useEditorInlineCommentsPubSub';
import { useEditorDraftSyncAction } from '@confluence/editor-features/entry-points/useEditorDraftSyncAction';
import { EditorWrapper } from '@confluence/editor-features/entry-points/EditorWrapper';
import { EditorDisplayWrapper } from '@confluence/editor-features/entry-points/EditorDisplayWrapper';
import { EditorLinkingFeatures } from '@confluence/editor-features/entry-points/EditorLinkingFeatures';
import { PageLoadWrapper } from '@confluence/editor-features/entry-points/PageLoadWrapper';
import { EditorPluginAIWithFeedback } from '@confluence/editor-features/entry-points/EditorPluginAIWithFeedback';
import { useEditorAIOptIn } from '@confluence/editor-features/entry-points/useEditorAIOptIn';
import { useScrollOnUrlChange } from '@confluence/editor-features/entry-points/useScrollOnUrlChange';
import { useEditorAIReplacingTitle } from '@confluence/editor-features/entry-points/useEditorAIReplacingTitle';
import { withSmartCardProviderContext } from '@confluence/editor-features/entry-points/withSmartCardProviderContext';
import { withDocChangeContext } from '@confluence/editor-features/entry-points/withDocChangeContext';
import {
	// HOCs
	withEditorActions,
} from '@confluence/editor-features/entry-points/withEditorActions';
import { EditorContextContentCreationPanelWrapper } from '@confluence/editor-templates/entry-points/EditorContextContentCreationPanelWrapper';
import { EditorContextPanelContextProvider } from '@confluence/editor-context-panel/entry-points/EditorContextPanelContextProvider';
import { EditorContextPanelView } from '@confluence/editor-context-panel-view';
import { useEditorAnnotationProviderOptions } from '@confluence/annotation-provider/entry-points/useEditorAnnotationProviderOptions';
import { EditorAnnotationProviderComponents } from '@confluence/annotation-provider-inline-comments/entry-points/editor';
import { useCommentRefetch } from '@confluence/inline-comments-hooks/entry-points/useCommentRefetch';
import { useEditorAnnotationsActions } from '@confluence/inline-comments-hooks/entry-points/useEditorAnnotations';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments';
import { useUpdateAnnotations } from '@confluence/comments-data/entry-points/useUpdateAnnotations';
// eslint-disable-next-line import/no-duplicates
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments';
import { useEditorSSRExtensionHandlers } from '@confluence/fabric-extension-handlers/entry-points/editorSSRExtensionHandlers';
import { useExtensionOptions } from '@confluence/fabric-extension-handlers/entry-points/useExtensionOptions';
import { getTaskDecisionProvider } from '@confluence/fabric-providers/entry-points/TaskDecisionProvider';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider';
import { useProfilecardProvider } from '@confluence/fabric-providers/entry-points/useProfileCardProvider';
import { useMentionsNotificationsActions } from '@confluence/mentions-notifications/entry-points/useMentionsNotifications';
import { MentionsNotificationsWrapper } from '@confluence/mentions-notifications/entry-points/MentionsNotificationsWrapper';
// Editor UI components
import { DocumentEditPageTitle } from '@confluence/document-title/entry-points/DocumentEditPageTitle';
import { EditorTypes, PreviewPageComponent } from '@confluence/editor-preview';
import {
	ContentAppearanceContextProvider,
	ContentAppearanceContextConsumer,
} from '@confluence/content-appearance/entry-points/ContentAppearanceContext';
import { type FullPageAppearances } from '@confluence/content-appearance/entry-points/contentAppearanceTypes';
import { getContentAppearanceForAtlaskit } from '@confluence/content-appearance/entry-points/atlaskitTransformations';
import { PageContentRenderer } from '@confluence/content-renderer/entry-points/PageContentRenderer';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext';
import {
	useEditorRevertDialogContext,
	withRevertContext,
} from '@confluence/editor-revert-dialog/entry-points/EditorRevertDialogContext';
import { EditorRevertDialog } from '@confluence/editor-revert-dialog/entry-points/EditorRevertDialog';
import { OnboardingEditorModalPrompt } from '@confluence/onboarding-editor';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	deserializeState,
	useGetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import type {
	EditorWrapperProps,
	ContentTypeType,
} from '@confluence/editor-features/entry-points/types';
import { EditorConnectionBannerWrapper } from '@confluence/browser-status-indicator/entry-points/EditorConnectionBannerWrapper';
import {
	useNewContentHistoryState,
	useNewContentHistoryEditorTransition,
} from '@confluence/content-history-new';
import { NewContentHistory } from '@confluence/content-history-new/entry-points/NewContentHistory';
import { EDITOR } from '@confluence/macro-tracker/entry-points/MacroExperienceTracker';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart';
import {
	useRouteActions,
	createSingleQueryParamHook,
} from '@confluence/route-manager/entry-points/RouteState';
import { withEditorModalContext } from '@confluence/editor-modal';
import { scrollToLastViewedPosition } from '@confluence/editor-scroll-position/entry-points/scrollToLastViewedPosition';
import { measureTimeToEdit } from '@confluence/editor-scroll-position/entry-points/measureTimeToEdit';
import {
	useNativeCollabState,
	useNativeCollabConnected,
} from '@confluence/native-collab/entry-points/useNativeCollabState';
import { useConnecting } from '@confluence/native-collab/entry-points/useConnecting';
import { CollabServiceProperty } from '@confluence/native-collab/entry-points/commonCollabTypes';
import type { NativeCollabInjectedProps } from '@confluence/native-collab/entry-points/commonCollabTypes';
import { isUnrecoverableError } from '@confluence/native-collab/entry-points/useError';
import {
	EditorContextConnectionsPanel,
	useConnectionsPanel,
} from '@confluence/app-referentiality/entry-points/EditorContextConnectionsPanel';
import { getReferentialityContext } from '@confluence/app-referentiality';
import { getMarketplaceAppsContainer } from '@confluence/app-suggestions/entry-points';
import { useEditSource } from '@confluence/edit-common/entry-points/useEditSource';
import { ConditionalPublish } from '@confluence/conditional-publish-dialog';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive';
import { useIsLivePagesFeatureEnabled } from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { ConvertToLivePageFlag } from '@confluence/live-pages-features/entry-points/ConvertToLivePageFlag';
import { useContentTypesTitleUpdate } from '@confluence/content-types-header/entry-points/useContentTypesTitleUpdate';
import { useUpdateLastEditedTimestampVersionCache } from '@confluence/content-types-utils/entry-points/useUpdateLastEditedTimestampVersionCache';
import { EditorObjectHeaderContextProvider } from '@confluence/content-types-header/entry-points/EditorObjectHeaderContextProvider';
import { useOptimisticTitleChange } from '@confluence/page-tree-refresh-state-container/entry-points/useOptimisticTitleChange';
import {
	EditorContextCustomSitesConfigPanelEntryPoint,
	useCustomExtensionConfigPanel,
} from '@confluence/custom-sites-extensions/entry-points/editorConfigPanel';
import {
	AutoConvertEvent,
	AutoConvertActionSubject,
	sendEditorConversionAnalyticsPageEvent,
} from '@confluence/editor-conversion/entry-points/analytics';
import { useAutoConvertState } from '@confluence/editor-conversion/entry-points/useAutoConvertState';
import { useSchedulePublishFlagsWatcher } from '@confluence/editor-schedule-publish/entry-points/useSchedulePublishFlagsWatcher';
import {
	PrimaryToolbarContextProvider,
	usePrimaryToolbarContext,
} from '@confluence/editor-primary-toolbar-components/entry-points/PrimaryToolbarContext';
import { EditorPrimaryToolbarComponents } from '@confluence/editor-primary-toolbar-components/entry-points/EditorPrimaryToolbarComponents';
import { ContentComponentsContextProvider } from '@confluence/title-area-content-components/entry-points/ContentComponentsContext';
import { EditorActionBar } from '@confluence/editor-action-bar/entry-points/EditorActionBar';
import { SmartCreateFeedbackBannerLoader } from '@confluence/editor-action-bar/entry-points/SmartCreateFeedbackBannerLoader';
import { ContentTypesHeader } from '@confluence/content-types-header/entry-points/ContentTypesHeader';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId';
import { BlankPageQuickActions } from '@confluence/blank-page-quick-actions/entry-points/BlankPageQuickActions';
import { BlankPageQuickActionsTemplatePreview } from '@confluence/blank-page-quick-actions/entry-points/BlankPageQuickActionsTemplatePreview';
import { BlankPageQuickActionsProvider } from '@confluence/blank-page-quick-actions/entry-points/BlankPageQuickActionsContext';
import { useShouldShowBlankPageQuickActions } from '@confluence/blank-page-quick-actions/entry-points/useShouldShowBlankPageQuickActions';
import { getBPQAMilestone3ExperimentEnabled } from '@confluence/blank-page-quick-actions/entry-points/getBlankPageQuickActionsExperimentEnabled';
import { useContextualInviteNudge } from '@confluence/onboarding-helpers/entry-points/hooks/useContextualInviteNudge';
import { useContentClassification } from '@confluence/data-classification/entry-points/queries';
import { InlineCommentMode } from '@confluence/inline-comments-utils/entry-points/enum';
import { useInlineCommentQueryParams } from '@confluence/comment/entry-points/useCommentQueryParams';
import { isCommentNotFoundError } from '@confluence/comments-util/entry-points/errorUtils';
import { getSortedMarkerRefs } from '@confluence/comments-util/entry-points/domUtils';
import { useHeadingLinksPathBuilder } from '@confluence/editor-heading-links/entry-points/useHeadingLinksPathBuilder';
import { HeadingLinksStyleWrapper } from '@confluence/editor-heading-links/entry-points/HeadingLinksStyleWrapper';
import { GuestMentionsNotificationsWrapper } from '@confluence/external-collab-ui/entry-points/GuestMentionsNotificationsWrapper';
import { HubEditorWrapper } from '@confluence/company-hub/entry-points/hub-layout';
import { MultiMacroQueryContainer } from '@confluence/fabric-extension-queries/entry-points/multiMacroQuery';
import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop';
import { useInviteFromLinkExperimentEditor } from '@confluence/invite-on-link-insert/entry-points/useInviteFromLinkExperimentEditor';
import { useRequestToEditContent } from '@confluence/no-permission/entry-points/useRequestToEditContent';
import { ConfluenceRecapModalLoadable } from '@confluence/confluence-recap/entry-points/ConfluenceRecapModal';
import { useExtensionRendererViewOptions } from '@confluence/fabric-extension-lib/entry-points/useExtensionRendererViewOptions';
import { useFullPageEditorApi } from '@confluence/full-page-editor-store';
import { PageFooterComments } from '@confluence/page-footer-comments';
import { PublishModalKeyboardShortcuts } from '@confluence/publish-modal/entry-points/PublishModalKeyboardShortcuts';
import { PublishModalMountPoint } from '@confluence/publish-modal/entry-points/PublishModalMountPoint';
import { usePrePublishTitleValidation } from '@confluence/publish-modal/entry-points/usePrePublishTitleValidation';
import { usePublishModalMountActions } from '@confluence/publish-modal/entry-points/PublishModalMountState';
import { useEmbeddedEditPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/edit-page';
import { useEmbeddedViewPageFeatures } from '@confluence/embedded-confluence-allowed-features/entry-points/view-page';
import type { LegacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';
import { ClassificationModalContextProvider } from '@confluence/data-classification/entry-points/ClassificationModalContext';
import { useAIRovoButtonEnablement } from '@confluence/ai-floating-context-menu/entry-points/useAIRovoButtonEnablement';
import { useExtensionNodeParams } from '@confluence/fabric-extension-legacy-macro-renderer/entry-points/useExtensionNodeParams';
import {
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext';
import { useStableCallback } from '@confluence/use-stable/entry-points/useStableCallback';
import { RovoBridgeCommandListener } from '@confluence/rovo-editor-bridge-listener/entry-points/RovoBridgeCommandListener';
import { ImproveWritingFlagContainer as ImproveWritingFlag } from '@confluence/experiment-improve-writing-on-paste/entry-points/ImproveWritingFlag';
import { useUnreadCommentsSetup } from '@confluence/unread-comments/entry-points/useUnreadCommentsSetup';
import { usePrintCoverInjector } from '@confluence/pdf-print-utils/entry-points/coverInjector';
import { usePrintIntercept } from '@confluence/pdf-print-utils/entry-points/print';

import { useLiveDocTransitionCallback } from './hooks/useLiveDocTransitionCallback';
import { FullPageComposableEditor } from './AkEditor/ComposableEditor';
import { OnboardingHelpContainer } from './OnboardingHelpContainer';
import {
	ANALYTICS_SOURCE,
	EASY_MENTIONS_PLACEHOLDER_TEXT_ID,
	EASY_MENTIONS_PLACEHOLDER_DEFAULT_MESSAGE,
	AI_PLACEHOLDER_TEXT_ID,
	AI_PLACEHOLDER_DEFAULT_MESSAGE,
} from './fullPageEditorConstants';
import { i18n } from './i18n';
import { useMacroFallbackFlag } from './hooks/useMacroFallbackFlag';
import { useEditorFullPageExperiments } from './hooks/useEditorFullPageExperiments';
import { EditorNavigationLockout, useHasUnsavedChanges } from './EditorNavigationLockout';
import { TitleAreaContentComponentsWrapper } from './TitleAreaContentComponentsWrapper';
import { useFullPageEditorLoomInsertion } from './hooks/useFullPageEditorLoomInsertion';
import { useStartLivePageEditExperience } from './hooks/useStartLivePageEditExperience';
import { useEditorContentDisabled } from './hooks/useEditorContentDisabled';
import { useEditorCollabConnectivitySync } from './hooks/useEditorCollabConnectivitySync';
import { useEditorActions } from './hooks/useEditorActions';
import { useFocusedComment } from './hooks/useFocusedComment';
import { FullPageEditorBannerWrapper } from './FullPageEditorBannerWrapper';
import { useFullPageAutoFocus } from './hooks/useFullPageAutoFocus';
import { ObjectSidebarControlFullPageEditor } from './ObjectSidebarControlFullPageEditor';
import { withRovoSystemSpaceNotFound } from './withRovoSystemSpaceNotFound';

export type FullPageEditorComponentProps = NativeCollabInjectedProps & {
	contentId: string;
	editorActions: EditorActions;
	spaceKey: string;
	spaceId: string | undefined;
	extensionContextProvider?: {
		ClassicEditorContextProvider: any;
	};
	/**
	 * defaults to true
	 * used by live pages as live pages render editor on view only mode
	 */
	hasEditPermission: boolean;
	/* supply customPerfData object to add custom properties to all `performance measured` events that use EDITOR_METRIC page segment */
	customPerfData?: Record<string, string | boolean | object>;
	onDraftSyncSuccess?: () => void; // Passed to useEditorDraftSyncAction as a callback when sync is successful.
	loadingPriority?: LoadingPriority | null;
	isFastLoadedLiveDoc?: boolean;
	hasByLineContributors: boolean;
	hasByLineExtensions: boolean;
	hasReactions: boolean;
	hasPageComments: boolean;
	hasInlineComments: boolean;
	isTemplateBrowserEnabled: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FullPageEditorWrapper = styled.div({
	isolation: 'isolate',
});

// IMPORTANT: If you change the code below, please also update the
// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/pastePluginOptions.ts
// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/cardPluginOptions.ts
const CARD_OPTIONS = { allowDatasource: true };

const useLinkCreationQueryParam = createSingleQueryParamHook('linkCreation');
const useFocusedCommentIdQueryParam = createSingleQueryParamHook('focusedCommentId');
const useFocusedMentionIdQueryParam = createSingleQueryParamHook('focusedMentionId');
const useReplyToCommentIdQueryParam = createSingleQueryParamHook('replyToComment');

// Content components are displayed below the editor tool bar
const _getContentComponents = memoize(
	({
		contentId,
		cloudId,
		debounceTitleChangeUpdates,
		focusEditor,
		onFocusChanged,
		userOffline,
		linkCreation,
		editorPluginAIFeedbackHandlers,
		isAdminHubAIEnabled,
		isAIEnabledForContent,
		hideSpinner,
		templateSuggestionsRef,
		templateButtonRef,
		viewMode,
		contentClassificationMetadata,
		spaceKey,
		isUnpublishedDraft,
		showBlankPageQuickActions,
		isLivePage,
		version,
		isEditorEmpty,
		shouldRenderPreview,
		shouldShowNewContentHistory,
		isEditorAnnotationProviderManagersEnabled,
		editorApi,
		contentType,
		editorActions,
		disabled,
		isTitleFocused,
		appearance,
		title,
		onTitleChange,
		cancelDebouncedSaveDraft,
		showSpinner,
		loadingPriority,
		isRovoButtonEnabled,
		editorSessionId,
		editorActionsProps,
		hasByLineContributors,
		hasByLineExtensions,
		hasReactions,
		hasPageComments,
		isEmbeddedConfluence,
		isTemplateBrowserEnabled,
		isSeamlessTransitionInProgress = false,
	}) => {
		const titleAreaContentComponents = (
			<ReactionsProvider contentId={contentId}>
				<TitleAreaContentComponentsWrapper
					isEditorEmpty={isEditorEmpty}
					contentId={contentId}
					cloudId={cloudId}
					focusEditor={focusEditor}
					onFocusChanged={onFocusChanged}
					debounceOnChange={debounceTitleChangeUpdates}
					userOffline={userOffline}
					templateSuggestionsRef={templateSuggestionsRef}
					templateButtonRef={templateButtonRef}
					linkCreation={linkCreation}
					hideSpinner={hideSpinner}
					viewMode={viewMode}
					contentClassificationMetadata={contentClassificationMetadata}
					version={version}
					editorApi={editorApi}
					editorSessionId={editorSessionId}
					isSeamlessTransitionInProgress={isSeamlessTransitionInProgress}
					hasByLineContributors={hasByLineContributors}
					hasByLineExtensions={hasByLineExtensions}
					hasReactions={hasReactions}
				/>
			</ReactionsProvider>
		);

		let blankPageQuickActionsTemplatePreview;
		const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

		const { isBPQAMilestone3ExperimentEnabled } = getBPQAMilestone3ExperimentEnabled({
			shouldFireExperimentExposure: true,
		});

		// show BPQA full size template preview when we
		// are not previewing the editor, BPQA should show,
		// milestone 3 is enabled, and it's either an
		// unpublished draft or a live page
		const shouldShowFullPageTemplatePreview =
			!shouldShowNewContentHistory &&
			!shouldRenderPreview &&
			showBlankPageQuickActions &&
			isBPQAMilestone3ExperimentEnabled &&
			(isUnpublishedDraft || isLivePage);

		if (shouldShowFullPageTemplatePreview) {
			blankPageQuickActionsTemplatePreview = (
				<BlankPageQuickActionsTemplatePreview spaceKey={spaceKey} />
			);
		}

		const isEmbeddedTemplateBrowserEnabled = isEmbeddedConfluence ? isTemplateBrowserEnabled : true;

		const shouldRenderBPQA =
			isEmbeddedTemplateBrowserEnabled &&
			!shouldShowNewContentHistory &&
			!shouldRenderPreview &&
			showBlankPageQuickActions &&
			!isCompanyHub;

		const shouldRenderPageFooterComments = isEmbeddedConfluence ? hasPageComments : true;

		// confluence_footer_comment_styling_change controls pinning the footer to the bottom on small live pages (see ObjectCommentContainer)
		// However, this can only happen on first paint in SPA (useEffect + the DOM aren't available in SSR), so the SSR'd version doesn't match the SPA version
		// Until we have a styling only fix, we should prevent rendering this in SSR (but keep preloading, etc to improve TTVC)
		const shouldRenderObjectCommentWrapper =
			fg('confluence_frontend_content_wrapper') &&
			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
			!(process.env.REACT_SSR && fg('confluence_footer_comment_styling_change'));

		return {
			before: [
				!isCompanyHub && titleAreaContentComponents,
				isAdminHubAIEnabled && (
					<EditorPluginAIWithFeedback
						key="editor-ai-feedback-collector"
						editorPluginAIFeedbackHandlers={editorPluginAIFeedbackHandlers}
					/>
				),
				isEditorAnnotationProviderManagersEnabled && (
					<EditorAnnotationProviderComponents key="editorAnnotationProviderComponents" />
				),
				<EditorForgeExtensionConfigHelperRenderer key="editorForgeExtensionConfigHelperRenderer" />,
				!isCompanyHub && (
					<ObjectSidebarControlFullPageEditor
						editorActionsProps={editorActionsProps}
						hasAiButton={isLivePage || isRovoButtonEnabled}
						hasAudioButton={
							// eslint-disable-next-line confluence-feature-gating/no-preconditioning
							isAIEnabledForContent && fg('cc_ai_audio_in_live_docs_object_sidebar')
						}
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
						contentTitle={title}
						editorApi={editorApi}
						isUnpublishedDraft={isUnpublishedDraft}
					/>
				),
				shouldRenderBPQA && (
					<BlankPageQuickActions
						editorActions={editorActions}
						disabled={disabled}
						isTitleFocused={isTitleFocused}
						focusEditor={focusEditor}
						appearance={appearance}
						spaceKey={spaceKey}
						isUnpublishedDraft={isUnpublishedDraft}
						title={title}
						onTitleChange={onTitleChange}
						contentId={contentId}
						cancelDebouncedSaveDraft={cancelDebouncedSaveDraft}
						showSpinner={showSpinner}
						hideSpinner={hideSpinner}
						isAIEnabled={isAdminHubAIEnabled}
						editorPresetAPI={editorApi}
					/>
				),
			],
			after: [
				blankPageQuickActionsTemplatePreview,
				shouldRenderPageFooterComments && !shouldRenderBPQA && isLivePage && (
					<>
						{contentType === 'page' &&
							expValEquals('confluence_ai_related_content_for_live_pages', 'isEnabled', true) && (
								<UFOLabel name="end-of-page-recommendation-loader">
									<EndOfPageRecommendationFinalLoader
										entityType={contentType}
										entityId={contentId}
										spaceKey={spaceKey ?? ''}
										pageWidthType={`"max-width"`}
										isLivePage={isLivePage}
									/>
								</UFOLabel>
							)}
						{!fg('confluence_frontend_content_wrapper') &&
							fg('confluence_frontend_comments_panel_v2') && (
								<UFOLabel name="editor-footer-comments">
									<PageFooterComments
										key={contentId}
										contentId={contentId}
										loadingPriority={loadingPriority}
									/>
								</UFOLabel>
							)}
						{shouldRenderObjectCommentWrapper && (
							<>
								<UFOLabel name="editor-footer-comments">
									<ObjectCommentWrapper
										showGeneralComment
										showLabels
										loadingPriority={loadingPriority}
										key={`ObjectCommentWrapper-${contentId}`} // ensures safe re-render on page change
										hasReactions={hasReactions}
									/>
								</UFOLabel>
								{fg('confluence_comment_sticky_footer_enabled') && <StickyDraftCommentContainer />}
							</>
						)}
					</>
				),
			],
		};
	},
	isEqual,
);

const _getContextPanelComponent = memoize(() => {
	return <EditorContextPanelView />;
});

const _getEditorContextConnectionsPanel = memoize(
	({
		referentialityContext,
		connectionsPanelActiveNodeLocalId,
		setConnectionsPanelActiveNodeLocalId,
		closeConnectionsPanel,
	}) => {
		if (connectionsPanelActiveNodeLocalId && referentialityContext?.api) {
			return (
				<EditorContextConnectionsPanel
					api={referentialityContext.api}
					activeNodeLocalId={connectionsPanelActiveNodeLocalId}
					setActiveNodeLocalId={setConnectionsPanelActiveNodeLocalId}
					closeConnectionsPanel={closeConnectionsPanel}
				/>
			);
		}

		return null;
	},
);

const _getEditorContextSmartButtonConfigPanel = memoize(
	({ smartButtonActiveNodeLocalId, closeSmartButtonConfigPanel, fullPageEditorPresetAPI }) => {
		if (smartButtonActiveNodeLocalId) {
			return (
				<EditorContextSmartButtonConfigPanel
					localId={smartButtonActiveNodeLocalId}
					closeSelf={closeSmartButtonConfigPanel}
					editorApi={fullPageEditorPresetAPI}
				/>
			);
		}
		return null;
	},
	isEqual,
);

type FeatureFlags = { [key: string]: string | number | boolean };

type PlaceholderTextParam = {
	isAIEnabledForContent: boolean;
	featureFlags: FeatureFlags;
};

// IMPORTANT: If you change the code below, please also update the
// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/placeholderPluginOptions/placeholderPluginOptions.ts
const _getPlaceholderText = memoize((placeholderTextParam: PlaceholderTextParam) => {
	const isAIEnabledForContent = placeholderTextParam.isAIEnabledForContent;

	if (isAIEnabledForContent && fg('platform_editor_ai_aifc_patch_beta')) {
		return i18n.editorEmptyDocumentSpaceShortcutPlaceholder;
	}

	if (isAIEnabledForContent && editorExperiment('platform_editor_ai_quickstart_command', true)) {
		return i18n.editorEmptyDocumentPlaceholderAI;
	}

	if (editorExperiment('platform_editor_controls', 'variant1')) {
		return i18n.editorEmptyDocumentPlaceholder;
	}

	const key = `${isAIEnabledForContent ? 'AI' : 'noAI'}`;

	const placeholders = {
		AI: {
			//  Placeholder text when AI is enabled
			id: AI_PLACEHOLDER_TEXT_ID,
			defaultMessage: AI_PLACEHOLDER_DEFAULT_MESSAGE,
		},
		noAI: {
			//  Placeholder text when AI is not enabled
			id: EASY_MENTIONS_PLACEHOLDER_TEXT_ID,
			defaultMessage: EASY_MENTIONS_PLACEHOLDER_DEFAULT_MESSAGE,
		},
	};

	return placeholders[key];
});

const withEditorContext = (component) =>
	withSmartCardProviderContext(withRevertContext(withEditorModalContext(component)));
const getContentAppearance = memoize((appearance, draftAppearance): FullPageAppearances => {
	// if the appearance is default value (version 0), reference the draftAppearance if it exists; otherwise, use appearance
	const contentAppearance =
		appearance.version === 0 && draftAppearance ? draftAppearance : appearance.appearance;

	return getContentAppearanceForAtlaskit(contentAppearance);
});

// If updating please update in DiffEditor.tsx as well
const _createLCMExtensionProvider = async (
	outerEditorView: EditorView | undefined,
	outerEditorApi: PublicPluginAPI<[ExtensionPlugin, LegacyContentMacroPlugin]> | undefined,
	// TODO:EDITOR-2435 remove `dense` when cleaning up 'confluence_content_mode_replace_dense_with_compact'
	propOverrides?: { contentMode?: 'standard' | 'dense' | 'compact' | undefined },
) => {
	const { createManifest } = await import(
		/* webpackChunkName: "loadable-legacy-content-macro" */ '@confluence/experiment-legacy-content-macro'
	);

	return new DefaultExtensionProvider([
		createManifest(outerEditorView, outerEditorApi, propOverrides) as unknown as ExtensionManifest,
	]);
};

const FullPageEditorComponentImpl = (
	props: FullPageEditorComponentProps &
		WithFlagsProps &
		WithDialogsProps &
		WithDocChangeContextProps,
) => {
	const {
		// Props from Loader
		contentId,
		spaceKey,
		collabEditProvider,
		collabEdit,
		collabEditState,
		heartbeatData,
		contentType,
		disabled,
		setForceDisabled,
		internetDisconnected,
		forceDisabled,
		draftShareId,
		refetchNativeCollab,
		isFirstParticipant,
		isSingleUserSession,
		isUnpublishedDraft,
		onTitleChange,
		title,
		version,
		viewMode,
		onPublishUpdateTitleInPageTree,
		optimisticDeleteItemInTree,
		customPerfData,
		// Props from editor-core
		editorActions,
		// Context Provider for macro
		extensionContextProvider,
		flags,
		dialogs,
		handleDocChange,
		/**
		 * defaults to true
		 * used by live pages as live pages render editor on view only mode
		 */
		hasEditPermission = true,
		createdDate,
		onDraftSyncSuccess,
		loadingPriority,
		isFastLoadedLiveDoc,
		hasByLineContributors = true,
		hasByLineExtensions = true,
		hasReactions = true,
		hasPageComments = true,
		hasInlineComments = true,
	} = props;

	const [{ fullPageEditorPresetAPI }, { setEditorApi, resetEditorApi }] = useFullPageEditorApi();

	// Ensures editorApi sweet state is cleared on unmount
	useEffect(() => {
		return () => {
			resetEditorApi();
		};
	}, [resetEditorApi]);

	// Recommended way of accessing collabEditProvider without promise wrapper.
	const [{ collabEditProvider: extractedCollabEditProvider }] = useNativeCollabState();
	const [{ collabEditConnected }] = useNativeCollabConnected();
	const spaceId = useSpaceId();
	const { triggerAnalyticsEvent } = useEditorAnalyticsEvent({
		contentId,
		contentType,
		subject: 'editPage',
		source: ANALYTICS_SOURCE,
		version,
		collabService: 'ncs',
	});

	const { requestToEditContent, hasRequestedEditPermission } = useRequestToEditContent({
		contentId,
		flags,
		triggerAnalyticsEvent,
	});

	const isClassicMentionsNotificationsEnrolled = useIsMentionOnRestrictedPageEligible();

	const { setEditorAnnotations } = useEditorAnnotationsActions();

	const isDocumentActive = useDocumentActive();

	const { isSmartButtonAllowed } = useSmartButtonPremiumGate();
	const readFragmentInterop = useReadFragmentInterop({
		fragmentSpec: gql`
			fragment FullPageEditorComponentReadFragment on MacroConnection {
				nodes {
					contentId
					macroId
					position
					name
					parameters
					body
					hasBody
				}
				pageInfo {
					endCursor
					hasNextPage
				}
			}
		`,
	});
	const { updateTitle: updateContentTypesTitle } = useContentTypesTitleUpdate(
		contentId,
		title || '',
	);

	const {
		collabEditInitialized,
		collabEditError,
		collabEditInternetLost,
		collabEditConnectionTimedOut,
	} = collabEditState;
	const intl = useIntl();
	const { cloudId, userId, featureFlags, isAdminHubAIEnabled, edition, orgId, environment } =
		useSessionData();
	const { push } = useRouteActions();
	const linkCreation = useLinkCreationQueryParam();
	const focusedCommentId = useFocusedCommentIdQueryParam();

	const replyToCommentId = useReplyToCommentIdQueryParam();
	const focusedMentionId = useFocusedMentionIdQueryParam();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { triggerSaveIndicator } = useContext(TriggerSaveIndicatorContext);

	const isEditorEmpty: boolean =
		useSharedPluginStateWithSelector(
			fullPageEditorPresetAPI,
			// @ts-ignore @ts-expect-error
			['empty'],
			// @ts-ignore @ts-expect-error
			(states) => !!states?.emptyState?.editorIsEmpty,
		) ?? editorActions?.isDocumentEmpty();

	const showBlankPageQuickActions = useShouldShowBlankPageQuickActions({
		contentType,
		collabEditInitialized,
		isEditorEmpty,
		viewMode,
	});

	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();

	const isLivePage = useIsCurrentPageLive();

	const templateSuggestionsRef = useRef<HTMLDivElement | null>(null);
	const templateButtonRef = useRef<HTMLElement | null>(null);

	const [{ editSource }] = useEditSource();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	const handleGqlError = useCallback(
		(error: Error, isCommentNotFound: boolean = false) => {
			experienceTracker.stopOnError({
				name: VIEW_INLINE_COMMENT_EXPERIENCE,
				error,
				attributes: {
					mode: isLivePage ? InlineCommentMode.LIVE : InlineCommentMode.EDIT,
					stopPoint: 'full-page-editor/FullPageEditorComponent',
					commentNotFound: isCommentNotFound,
				},
			});
		},
		[experienceTracker, isLivePage],
	);

	// Note that this will detect missing inline and footer comments because the query will
	// find either type of comment - so if the comment doesn't exist, we will get the "not
	// found" error
	const handleUseFocusedCommentGQLError = useCallback(
		(error: Error) => {
			const isCommentNotFound = error ? isCommentNotFoundError(error.message) : false;
			if (flags && isCommentNotFound) {
				const commentId = focusedCommentId || replyToCommentId;
				const FULLPAGEEDITOR_COMMMENT_LINK_NOT_FOUND_FLAG_ID = `fullpageeditor-comment-link-not-found-${commentId}`;
				void flags.showWarningFlag({
					id: FULLPAGEEDITOR_COMMMENT_LINK_NOT_FOUND_FLAG_ID,
					title: <FormattedMessage {...i18n.commentLinkNotFoundFlagTitle} />,
					description: <FormattedMessage {...i18n.commentLinkNotFoundFlagDescription} />,
				});
			}
			handleGqlError(error, isCommentNotFound);
		},
		[flags, focusedCommentId, handleGqlError, replyToCommentId],
	);

	useExposeGoogleMeetScreenShare();

	// === Editor State ===
	const [shouldRenderPreview, setShouldRenderPreview] = useState<boolean>(false);
	const [previewAsBlog, setPreviewAsBlog] = useState<boolean>(false);
	const [stagedContentId, setStagedContentId] = useState<string | undefined>();
	const [stagedSpaceKey, setStagedSpaceKey] = useState<string | undefined>();
	const [proseMirrorRenderedData, setProseMirrorRenderedData] =
		useState<ProseMirrorRenderedDataType | null>(null);
	const [checkIfConditionPreventingPublish, setCheckIfConditionPreventingPublish] =
		useState<() => Promise<boolean>>();
	const assignConditionalFunction = useCallback(
		(conditionalFunction: any) => setCheckIfConditionPreventingPublish(() => conditionalFunction),
		[],
	);

	const { updateUnresolvedInlineComment } = useInlineCommentsActions();

	// importing the comments data states here as a temporary workaround to fix circular dependency, we need these states to handle dangling and unread from unread-comments package
	// unread-comments package is using comments-data package so we can't import comments-data into unread-comments package
	const [
		{ orderedActiveAnnotationIdList, commentsDataMap },
		{ setOrderedActiveAnnotationIdList, clearCommentsData, addNewCommentThreads },
	] = useCommentsData();
	const { clearMentionsFlags } = useMentionsNotificationsActions();
	const { resetInitialDataLoadedMap, setCurrentView } = useCommentsPanelActions();
	const { updateUnreadComments, updateUnreadCommentsOnFirstRender } = useUnreadCommentsActions();
	const { hideObjectSidebar } = useObjectSidebarActions();
	const { setActiveHighlight } = useInlineCommentsDispatchContext();
	const { shouldShowNewContentHistory } = useNewContentHistoryState();
	useNewContentHistoryEditorTransition(isLivePage, contentId);
	useScrollOnUrlChange(proseMirrorRenderedData !== null);

	const handleCollabError = useHandleCollabError(flags);
	const { data: onboardingStateData, loading: onboardingStateLoading } = useGetOnboardingState(
		Object.values(EDITOR_ONBOARDING_STATE_KEYS),
		!fg('cc_onboarding_experience') || version !== 1,
	);

	// when a user is eligible for contextual invite nudge, tag them before they publish
	void useContextualInviteNudge();

	const { editorOnboardingEligiblity, recentSpotlightCompleted } =
		deserializeState(onboardingStateData);

	const shouldShowRecentSpotlight =
		!onboardingStateLoading && editorOnboardingEligiblity && !recentSpotlightCompleted; // Makes sure recentSpotlightCompleted is not undefined

	const { onEditorReady, startingLinks } = useInviteFromLinkExperimentEditor();

	// === Editor common functionalities and providers ===
	const { collabLimitReached, heartbeatError, stopHeartbeatCallback, editorSessionId } =
		heartbeatData;

	// Edit page draft syncing
	const {
		cancelDebouncedSaveDraft,
		triggerSaveDraft,
		navigateOnClose,
		onPublish,
		isPublishingContent,
		saveDraft,
		saveDraftError,
	} = useDraftSyncAndPublish({
		contentId,
		contentType,
		refetchNativeCollab,
		isFirstParticipant,
		isUnpublishedDraft,
		spaceKey,
		title,
		version,
		onCollabError: handleCollabError,
		editorActions,
		checkIfConditionPreventingPublish,
		flags,
		dialogs,
		editorPresetAPI: fullPageEditorPresetAPI,
		editorSessionId,
		onPublishUpdateTitleInPageTree,
		optimisticDeleteItemInTree,
		startingLinks,
	});

	// Live doc draft syncing
	const { draftSyncActionError } = useEditorDraftSyncAction({
		contentId,
		editorPresetAPI: fullPageEditorPresetAPI,
		editorActions,
		onDraftSyncSuccess,
		isFirstParticipant,
	});

	const hasUnsavedChanges = useHasUnsavedChanges({
		contentId,
		isPublishingContent,
		isFirstParticipant,
		editorPresetAPI: fullPageEditorPresetAPI,
	});
	const printLockout = usePrintIntercept({
		enabled: expValEquals('afm-enable-command-p-reroute', 'isEnabled', true),
		shouldBlockPrint: () => hasUnsavedChanges,
	});

	usePrintCoverInjector(expValEquals('afm-enable-command-p-reroute', 'isEnabled', true), intl);

	const { isTransitionPreloading: isLiveDocSeamlessTransitionPreloading } =
		useLiveDocTransitionCallback({
			contentId,
			collabEditProvider: extractedCollabEditProvider,
			isPublishingContent,
			isFirstParticipant,
			editorPresetAPI: fullPageEditorPresetAPI,
			isLivePage,
			hasEditPermission,
			isFastLoadedLiveDoc,
		});

	const [isLiveDocSeamlessTransitioning, setIsLiveDocSeamlessTransitioning] =
		useState<boolean>(false);

	useEffect(() => {
		if (isLivePage) {
			if (!isLiveDocSeamlessTransitioning && isLiveDocSeamlessTransitionPreloading) {
				setIsLiveDocSeamlessTransitioning(true);
			} else if (
				isLiveDocSeamlessTransitioning &&
				!disabled &&
				!isLiveDocSeamlessTransitionPreloading
			) {
				setIsLiveDocSeamlessTransitioning(false);
			}
		} else if (isLiveDocSeamlessTransitioning) {
			setIsLiveDocSeamlessTransitioning(false);
		}
	}, [
		contentId,
		disabled,
		isLiveDocSeamlessTransitionPreloading,
		isLivePage,
		isLiveDocSeamlessTransitioning,
	]);

	const [isAutoConverted] = useAutoConvertState();

	const { draftTitle, draftBody, draftAppearance } = useInjectDraftContent({
		contentId,
		draftShareId,
		collabEditInitialized,
		editorActions,
		isUnrecoverableCollabEditError: !!collabEditError && isUnrecoverableError(collabEditError),
	});

	const {
		connectionsPanelActiveNodeLocalId,
		setConnectionsPanelActiveNodeLocalId,
		openConnectionsPanel,
		closeConnectionsPanel,
	} = useConnectionsPanel();

	const {
		activeNodeLocalId,
		openConfigPanel,
		closeConfigPanel,
		upsellModalSource,
		setUpsellModalSource,
	} = useCustomExtensionConfigPanel(editorActions);

	const { smartButtonActiveNodeLocalId, openSmartButtonConfigPanel, closeSmartButtonConfigPanel } =
		useSmartButtonConfigPanel();

	const { isAIEnabledForContent } = useIsAIEnabledForContent({ contentId });

	const { optInStatus, triggerOptInFlow, AIOptInModal } = useEditorAIOptIn({
		isAdminHubAIEnabled,
		isAIEnabledForContent,
		edition,
		orgId,
		environment,
	});

	const aiGlobalOptIn = useMemo(
		() => ({
			status: optInStatus,
			triggerOptInFlow,
		}),
		[optInStatus, triggerOptInFlow],
	);

	const { editorPluginAIFeedbackHandlers } = useEditorPluginAIFeedbackHandlers();
	const { onAIProviderChanged } = useEditorPluginAICollabConnector();

	const getChannelVariables = useGetChannelVariables(cloudId, userId || 'anonymous', orgId);

	const mediaOptions = useMediaOptions({
		contentId,
		contentType,
		flags,
		viewMode,
		editorAPI: fullPageEditorPresetAPI ?? undefined,
	});

	const handleFeedbackClicked = useCallback(
		(props) => {
			editorPluginAIFeedbackHandlers.current.handleFeedbackClicked(props);
		},
		[editorPluginAIFeedbackHandlers],
	);

	const handleReviewStateEntered = useCallback(() => {
		editorPluginAIFeedbackHandlers.current.handleReviewStateEntered();
	}, [editorPluginAIFeedbackHandlers]);

	const handleExperienceEnded = useCallback(() => {
		editorPluginAIFeedbackHandlers.current.handleExperienceEnded();
	}, [editorPluginAIFeedbackHandlers]);

	const editorPluginAIProviders = useMemo(
		() => ({
			mediaProvider: mediaOptions.provider!,
		}),
		[mediaOptions.provider],
	);

	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();
	const { isCommentsPanelShown, showCommentsPanel } = useShowCommentsPanel();

	const handleAnnotationsRemovedByAI = useStableCallback((ids: string[]) => {
		if (!ids?.length) {
			return;
		}

		if (ids.length) {
			void flags?.showInfoFlag({
				id: ids[0],
				title: intl.formatMessage(i18n.commentRemovedByRovoTitle, {
					count: ids.length,
				}),
				description: intl.formatMessage(i18n.commentRemovedByRovoDescription, {
					count: ids.length,
				}),
				isAutoDismiss: true,
				actions: fg('confluence_frontend_comments_panel_v2')
					? [
							{
								content: intl.formatMessage(i18n.commentRemovedByRovoViewCommentCTA, {
									count: ids.length,
								}),
								onClick: () => {
									const openWithView = fg('confluence_frontend_new_dangling_comments_ux')
										? ViewValues.DELETED_CONTENT
										: ViewValues.OPEN;

									if (!isCommentsPanelShown) {
										showCommentsPanel({ openWithView });
									} else {
										setCurrentView(openWithView);
									}

									// If multiple comments were removed then we will only scroll to the first in the list of comments
									setTimeout(() => {
										scrollCommentsPanelToThread({
											annotationId: ids[0],
											scrollOnLoad: true,
											isCommentThreadDangling: true,
										});
									}, 1000);

									void flags.hideFlag(flags.state.flags[0]?.id);
								},
							},
						]
					: [],
			});
		}
	});

	const editorPluginAIOptions = useEditorPluginAIOptions({
		aiGlobalOptIn,
		appearance: 'full-page',
		onFeedbackClicked: handleFeedbackClicked,
		onReviewStateEntered: handleReviewStateEntered,
		onExperienceComplete: handleExperienceEnded,
		onAIProviderChanged,
		getChannelVariables,
		providers: editorPluginAIProviders,
		contentId,
		onAnnotationsRemoved: handleAnnotationsRemovedByAI,
		usePlatformPrompt: fg('aifc_create_enabled'),
	});

	useSendContentAgeAnalytics({ contentId, collabService: 'ncs' });

	const {
		isTitleFocused,
		shouldFocusEditor,
		debounceTitleChangeUpdates,
		focusEditor,
		onFocusChanged,
	} = useEditorTitleFocus({
		contentType,
		editorActions,
		isUnpublishedDraft,
	});

	// Editor Disabled Analytics
	useDisabledAnalytics({
		contentId,
		disabled: isTitleFocused || disabled,
		shouldRenderPreview,
		shouldShowNewContentHistory,
		isTitleFocused,
		collabEditError,
		collabLimitReached,
		heartbeatError,
	});

	const updateUnresolvedInlineCommentFn = useUpdateAnnotations([
		updateUnresolvedInlineComment,
		setOrderedActiveAnnotationIdList,
	]);
	const annotationProviderOptions = useEditorAnnotationProviderOptions({
		pageId: contentId,
		triggerAnalyticsEvent,
		editorActions,
		updateUnresolvedInlineComment: fg('confluence-frontend-comments-panel')
			? updateUnresolvedInlineCommentFn
			: updateUnresolvedInlineComment,
	});
	const activityProvider = useActivityProvider();
	const mentionProvider = useMentionProvider(spaceId, undefined, focusedMentionId);
	const profilecardProvider = useProfilecardProvider({
		push,
		cloudId,
		featureFlags,
		userId,
	});
	const mentionProps = useMemo(() => {
		return {
			profilecardProvider,
		};
	}, [profilecardProvider]);
	const searchProvider = useSearchProvider();
	const findReplaceOptions = getFindReplaceOptions();
	const taskDecisionProvider = getTaskDecisionProvider();
	const autoformattingProvider = useAutoformattingProvider({
		contentId,
		spaceKey,
		source: ANALYTICS_SOURCE,
	});
	const emojiProvider = getEmojiProvider(EmojiResource, {
		cloudId,
		userId: userId || undefined,
		allowAnonymousAccess: true,
	});

	const contextIdentifierProvider = useContextIdentifierProvider(spaceId, contentId);

	const { fetchProvider, writeProvider } = useMemoizedContentAPIProviders(cloudId);
	const { fetchProvider: blockServiceFetchProvider, writeProvider: blockServiceWriteProvider } =
		useMemoizedBlockServiceAPIProviders();

	const syncedBlockProvider = useMemoizedSyncedBlockProvider(
		fg('platform_synced_blocks_block_service_provider') ? blockServiceFetchProvider : fetchProvider,
		fg('platform_synced_blocks_block_service_provider') ? blockServiceWriteProvider : writeProvider,
		getConfluencePageAri(contentId, cloudId, contentType === 'page' ? 'page' : 'blogpost'),
		{
			parentDataProviders: {
				emojiProvider,
				mediaProvider: mediaOptions.provider,
				mentionProvider,
				profilecardProvider: mentionProps?.profilecardProvider,
				taskDecisionProvider,
			},
			providerCreator: {
				createEmojiProvider: undefined,
				createMediaProvider: undefined,
			},
		},
	);

	const { showMacroFallbackFlag } = useMacroFallbackFlag(flags, isLivePage);
	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);

	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();

	// Extension V2 API props
	const { extensionOptions, rendererExtensionOptions } = useExtensionOptions({
		contentId,
		contentType,
		editorActions,
		mentionProvider,
		spaceKey,
		spaceId,
		extensionContextProvider,
		openConnectionsPanel,
		openConfigPanel,
		openSmartButtonConfigPanel,
		onCompleteCallback: showMacroFallbackFlag,
		isLivePage,
		allowCreateDatabaseExtension: true,
		allowDatabaseQuickInsert: true,
		allowCreateWhiteboardExtension: true,
		flags,
		aiOptInStatus: optInStatus,
		editorAPI: fullPageEditorPresetAPI ?? undefined,
		setUpsellModalSource,
		isFullPageEditor: true,
		allowCompanyHubExclusiveExtensions: isCompanyHub,
		isLivePagesFeatureEnabled,
		allowLegacyMacros: true,
		isCollabEditForLiveUpdatesEnabled: fg('confluence_toc_safari_bug_fix'),
	});

	useUnreadCommentsSetup({
		pageId: contentId,
		pageContentTypeProps: {
			pageMode: 'edit',
			orderedActiveAnnotationIdList,
			commentsDataMap,
			addNewCommentThreads,
		},
	});

	const { mode: editorContentMode } = useEditorContentModeValue({ contentId });

	const extensionProvidersWithLCM = useMemo(() => {
		const outerEditorView = editorActions._privateGetEditorView();

		const LCMExtension = _createLCMExtensionProvider(outerEditorView, fullPageEditorPresetAPI, {
			contentMode: editorContentMode,
		});

		return [...extensionOptions.extensionProviders, LCMExtension];
	}, [
		editorActions,
		fullPageEditorPresetAPI,
		extensionOptions.extensionProviders,
		editorContentMode,
	]);

	const { showRevertDialog } = useEditorRevertDialogContext();
	const { showSpinner, hideSpinner, shouldShowSpinner } = useSpinner();

	const editorAnnotationsRef = useRef<string[]>([]);
	const annotationStateRef = useRef<InlineCommentPluginState | undefined>();
	const { removeCommentQueryParams } = useInlineCommentQueryParams();

	usePluginStateEffect(fullPageEditorPresetAPI as any, ['annotation'], ({ annotationState }) => {
		const annotations = annotationState?.annotations;
		const { selectedAnnotations, mouseData } = annotationState || {};
		annotationStateRef.current = annotationState;

		const handleCommentQueryParams = () => {
			if (
				selectedAnnotations &&
				selectedAnnotations.length > 0 &&
				mouseData?.isSelecting &&
				(focusedCommentId || replyToCommentId)
			) {
				removeCommentQueryParams();
			}
		};

		const setAnnotations = () => {
			const editorAnnotationsToSet = annotations ? Object.keys(annotations) : [];

			if (fg('confluence_frontend_unread_comments_fixes')) {
				setEditorAnnotations(getSortedMarkerRefs(editorAnnotationsToSet, 'edit'), contentId);
			} else {
				setEditorAnnotations(getSortedMarkerRefs(editorAnnotationsToSet, 'edit'));
			}
			editorAnnotationsRef.current = editorAnnotationsToSet;
			const activeEditorAnnotationsToSet = annotations
				? Object.keys(annotations).filter((key) => annotations[key] === false)
				: [];
			setOrderedActiveAnnotationIdList(getSortedMarkerRefs(activeEditorAnnotationsToSet, 'edit'));
		};

		handleCommentQueryParams();
		setAnnotations();
	});

	useFocusedComment({
		commentId: focusedCommentId || replyToCommentId,
		contentId,
		handleGqlError: handleUseFocusedCommentGQLError,
		annotationState: annotationStateRef.current,
	});

	const { storedCommentData } = useAnnotations();
	const { setStoredCommentData } = useAnnotationsDispatch();

	useEditorInlineCommentsPubSub({
		contentId,
		contentType,
		triggerAnalyticsEvent,
		updateUnresolvedInlineComment: fg('confluence-frontend-comments-panel')
			? updateUnresolvedInlineCommentFn
			: updateUnresolvedInlineComment,
		isDocumentActive,
		storedCommentData,
		setStoredCommentData,
	});

	const { retrieveEditorTitleAndContent } = useRetrieveEditorTitleAndContent({
		onCollabError: handleCollabError,
		editorActions,
	});

	useCommentRefetch({
		isDocumentActive,
		previousAnnotations: editorAnnotationsRef.current,
		contentId,
		triggerAnalyticsEvent,
		updateUnresolvedInlineComment: fg('confluence-frontend-comments-panel')
			? updateUnresolvedInlineCommentFn
			: updateUnresolvedInlineComment,
		handleGqlError,
		updateUnreadComments,
		updateUnreadCommentsOnFirstRender,
		adfAnnotations: annotationStateRef.current?.annotations,
	});

	// TODO: add missing configs/providers (possibly not all):
	// presenceProvider
	// legacyImageUploadProvider
	// placeholderBracketHint
	// allowAnalyticsGASV3
	// onDestroy

	const wrapperProps: EditorWrapperProps = {
		attribution: Attribution.PAGE_EXTENSIONS,
		collabEditState,
		contentId,
		forceDisableEditor: setForceDisabled,
		isUnpublishedDraft,
		contentType,
		editorActions,
		heartbeatError,
		saveDraftError: isLivePage ? draftSyncActionError : saveDraftError,
		stopHeartbeatCallback,
		isLivePage,
		editorDisabled: disabled,
	};

	const performanceTrackingOptions = undefined;

	const editorFeatureFlags = useEditorFeatureFlags();
	const editorExperiments = useEditorFullPageExperiments();

	useConnecting(extractedCollabEditProvider);

	// Fetch content's classification data

	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext: isLivePage ? 'current-and-draft' : 'draft-only',
	});

	useEffect(() => {
		if (collabEditInitialized && !disabled) {
			scrollToLastViewedPosition(contentId);
		}
	}, [collabEditInitialized, disabled, contentId]);

	useEffect(() => {
		// DTR-1401-CFE
		// HOT-103036

		// If a blogpost is selected from in-editor templates panel, any preset restrictions are lost.
		// This lets the user know to update their blog restrictions.
		const showBlogpostAccessFlag = localStorage.getItem(keys.BLOGPOST_PERMISSIONS_NOTICE);

		if (flags && showBlogpostAccessFlag === 'true') {
			void flags.showInfoFlag({
				title: <FormattedMessage {...i18n.blogpostPermissionsNotice} />,
			});
			localStorage.removeItem(keys.BLOGPOST_PERMISSIONS_NOTICE);
		}
	}, [flags]);

	useEffect(() => {
		document.addEventListener('keydown', () => measureTimeToEdit(triggerAnalyticsEvent), {
			once: true,
		});
	}, [triggerAnalyticsEvent]);

	// Close the object sidebar when navigating to a new page
	useEffect(() => {
		void hideObjectSidebar();
	}, [contentId, hideObjectSidebar]);

	useEffect(() => {
		return () => {
			if (fg('confluence-frontend-comments-panel')) {
				resetInitialDataLoadedMap();
				clearCommentsData();
			}
			clearMentionsFlags();
			setActiveHighlight(null);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId]);

	// Focus the main content area for live pages Full Page Editor mount.
	useFullPageAutoFocus(isLivePage);

	const onProseMirrorRendered = (proseMirrorRenderedAttributes) => {
		proseMirrorRenderedAttributes = proseMirrorRenderedAttributes || {};
		// "distortedDuration" gets a true value if page was loaded in the background
		// in a non-visible tab which can lead to inaccurate duration numbers,
		// this can help us to capture only legitimate durations in our metric dashboards.
		// See https://bitbucket.org/atlassian/atlassian-frontend/src/master/packages/editor/editor-common/src/utils/performance/measure-render.ts
		const {
			proseMirrorRenderedFMP,
			proseMirrorRenderedTimestamp,
			startTime,
			duration,
			nodes,
			nodeSize,
			nodeSizeBucket,
			totalNodes,
			nodesInViewport,
			distortedDuration,
		} = proseMirrorRenderedAttributes;
		setProseMirrorRenderedData({
			proseMirrorRenderedFMP,
			proseMirrorRenderedTimestamp,
			startTime,
			duration,
			nodes,
			nodesInViewport,
			distortedDuration,
		});
		if (expValEquals('cc_editor_interactivity_monitoring', 'isEnabled', true)) {
			insm.endHeavyTask('PageLoad');
		}
		if (expValEquals('cc_editor_insm_doc_size_stats', 'isEnabled', true)) {
			insm.session?.addProperties({
				'initial:totalNodes': totalNodes,
				'initial:nodeSize': nodeSize,
				'initial:nodes': nodes,
				'initial:nodesInViewport': nodesInViewport,
				'initial:renderDuration': duration,
				'initial:nodeSizeBucket': nodeSizeBucket,
			});
		}
	};
	useEffect(() => {
		waitForGlobal(
			{
				listener: 'FullPageEditorComponent',
				globalProperty: 'AJS.bind',
			},
			() => {
				(window as any).AJS.bind('createGASv3Event', (_, data) => {
					createAnalyticsEvent(data).fire();
				});
			},
		);

		return () => {
			waitForGlobal(
				{
					listener: 'FullPageEditorComponent',
					globalProperty: 'AJS.unbind',
				},
				() => {
					(window as any).AJS.unbind('createGASv3Event');
				},
			);
		};
	}, [createAnalyticsEvent]);

	useEffect(() => {
		isAutoConverted &&
			sendEditorConversionAnalyticsPageEvent(
				createAnalyticsEvent,
				{
					pageAction: AutoConvertEvent.EDITED,
					source: 'FullPageEditorComponent-Next',
				},
				AutoConvertActionSubject.AUTO_CONVERSION,
			);
	}, [isAutoConverted, createAnalyticsEvent]);

	const { setOptimisticTitle, optimisticTitle } = useOptimisticTitleChange(contentId, title);
	const updateTimestampCache = useUpdateLastEditedTimestampVersionCache();

	const userOffline = collabEditInternetLost || collabEditConnectionTimedOut;

	const handleTitleChange = useStableCallback((newTitle: string) => {
		if (isLivePage) {
			/*
				Live pages immediately persist title changes to the backend and change the page title in the UI,
				unlike regular pages where changing the title only alters the draft version of the page and
				it is not fully persisted until the user saves the draft.
				*/
			updateContentTypesTitle(newTitle);
			return;
		}

		setOptimisticTitle(newTitle);

		triggerSaveIndicator();
		onTitleChange(newTitle);
		triggerSaveDraft();

		// Update timestamp cache so that Last Edited in object header responsively updates
		if (fg('confluence_frontend_content_wrapper')) {
			updateTimestampCache({
				contentId,
			});
		}
	});

	useStartLivePageEditExperience({
		editorApi: fullPageEditorPresetAPI || undefined,
		contentId,
	});

	const { openPublishModal } = usePublishModalMountActions();
	const validatePageAndOpenModal = usePrePublishTitleValidation({
		contentId,
		contentType,
		isUnpublishedDraft,
		flags,
		retrieveEditorTitleAndContent,
		onBeforeValidation: useCallback(() => {
			void saveDraft?.();
		}, [saveDraft]),
		onValidationSuccess: openPublishModal,
		analyticsSource: 'LivePagesDetailsPanel',
		optimisticTitle,
	});

	useEditorAIReplacingTitle(isAIEnabledForContent, handleTitleChange);

	const handleBreadcrumbsClick = useCallback(
		(destinationUrl: string) => {
			// https://product-fabric.atlassian.net/browse/CEMS-2479
			// sync draft and content title with NCS
			// when user click on breadcrumbs to exit the editor
			// so title is up-to-date in page tree
			return new Promise<void>((resolve) => {
				void saveDraft({
					isOnClose: true,
				}).then(() => {
					push(destinationUrl);
					resolve();
				});
			});
		},
		[push, saveDraft],
	);

	// [ED-27497] IMPORTANT: This has been moved to the below function. If you add anything here, also add it to the new function.
	// `quickInsertPluginOptions` function in 'packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/quickInsertPluginOptions.ts'.
	const elementBrowser = useMemo(
		() => ({
			showModal: true,
			replacePlusMenu: true,
			emptyStateHandler: getMarketplaceAppsContainer,
		}),
		[],
	);

	// IMPORTANT: If you add a new feature flag here, please make sure to add it to the
	// `featureFlagsPluginOptions` function in 'packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/featureFlagsPluginOptions.ts`.
	const fullPageEditorFeatureFlags = useMemo(
		() => ({
			...editorFeatureFlags,
			...editorExperiments,
			...{
				'basic-text-transformations':
					// utilise this flag as part of eligibility check to avoid passing AI enablement status as editor prop
					aiGlobalOptIn.status === 'disabled',
			},
		}),
		[editorFeatureFlags, editorExperiments, aiGlobalOptIn.status],
	);

	const onPreviewClicked = useCallback(
		(requestingPreviewAsBlog: any, stagedMoveParams: any) => {
			setPreviewAsBlog(requestingPreviewAsBlog === true);
			setShouldRenderPreview(true);
			setStagedContentId(stagedMoveParams?.newParentPageId);
			setStagedSpaceKey(stagedMoveParams?.newSpaceKey);
		},
		[setPreviewAsBlog, setShouldRenderPreview, setStagedContentId, setStagedSpaceKey],
	);

	const onClickPublish = useStableCallback(onPublish);
	const navigateOnCloseStable = useStableCallback(navigateOnClose);

	useSchedulePublishFlagsWatcher({
		contentId,
		contentType,
		isUnpublishedDraft,
		title: title ?? draftTitle,
		flags,
		isSchedulePublishEnabled: !isLivePage,
	});

	// Insert Loom video at top of editor page if a Loom url exists
	useFullPageEditorLoomInsertion(fullPageEditorPresetAPI);

	const { isExternalCollaborator } = useIsExternalCollaborator();

	const { getHeadingLinkPath } = useHeadingLinksPathBuilder({
		contentId,
		spaceKey,
		contentType,
		title,
		createdDate,
		isUnpublishedDraft,
	});

	const editorDisabled = useEditorContentDisabled({
		disabled,
		collabEditInternetLost,
		internetDisconnected,
		api: fullPageEditorPresetAPI,
	});

	useEditorCollabConnectivitySync({
		collabEditInternetLost,
		internetDisconnected,
		editorFullPagePresetAPI: fullPageEditorPresetAPI,
	});

	const extensionRendererViewOptions = useExtensionRendererViewOptions({
		getExtensionHandlers: rendererExtensionOptions?.getExtensionHandlers,
		isLivePage,
		contentId,
		spaceKey,
	});

	const getExtensionHeight = useExtensionNodeParams();

	const { isDesktop } = useIsDesktop();

	const finalExtensionProviders = extensionProvidersWithLCM;

	const ssrExtensionHandlers = useEditorSSRExtensionHandlers(
		contentId,
		spaceKey,
		finalExtensionProviders,
	);

	const FullPageEditorComponentWrapper = isCompanyHub ? HubEditorWrapper : FullPageEditorWrapper;

	const { canUndo, canRedo, undo, redo, activateFindReplace } =
		useEditorActions(fullPageEditorPresetAPI);

	const editorActionsProps =
		fg('aifc_create_enabled') || editorExperiment('platform_editor_controls', 'variant1')
			? {
					canUndo,
					canRedo,
					undo,
					redo,
					activateFindReplace,
				}
			: undefined;

	const allowInlineComments = isEmbeddedConfluence ? hasInlineComments : true;

	const allowPageComments = isEmbeddedConfluence ? hasPageComments : true;

	const { isTemplateBrowserEnabled } = useEmbeddedEditPageFeatures();
	const { hasModernHeader, hideHelpButton } = useEmbeddedViewPageFeatures();
	const showEmbeddedModernHeader = isEmbeddedConfluence ? hasModernHeader : !isEmbeddedConfluence;

	const showHelpWidget = isEmbeddedConfluence ? !hideHelpButton : true;

	return (
		<UFOLabel name="full-page-editor-component">
			<SaveIndicatorContextProvider editorApi={fullPageEditorPresetAPI}>
				<EditorContextPanelContextProvider editorAPI={fullPageEditorPresetAPI}>
					<ClassificationModalContextProvider>
						<EditorContextContentCreationPanelWrapper
							cancelDebouncedSaveDraft={cancelDebouncedSaveDraft}
							collabEditInitialized={collabEditInitialized}
							contentId={contentId}
							contentType={contentType}
							draftShareId={draftShareId}
							editorActions={editorActions}
							hideSpinner={hideSpinner}
							isUnpublishedDraft={isUnpublishedDraft}
							renderPanel={!shouldRenderPreview || !shouldShowNewContentHistory}
							showSpinner={showSpinner}
							title={title}
						>
							{({
								isContentCreationPanelOpen,
								onClickOpenContentCreationPanel,
								beforeUpdateRequest,
								postUpdateRequest,
							}) => (
								<ContentAppearanceContextProvider contentId={contentId}>
									<ContentAppearanceContextConsumer>
										{({ contentAppearance }) => {
											let appearance: FullPageAppearances = getContentAppearance(
												contentAppearance,
												draftAppearance,
											);

											if (isCompanyHub) {
												// Company Hub editors no longer have the option of changing page width,
												// as content topper is removed. For users who had previously set Company
												// Hub page to 'full-width' and can no longer change it back, we force it to
												// 'full-page' to fix unexpected layout issues and allow editors to
												// set the width on individual extension instances.
												appearance = 'full-page';
											}
											const referentialityContext = getReferentialityContext(
												createAnalyticsEvent,
												contentId,
												appearance,
												ssrExtensionHandlers,
												extensionRendererViewOptions,
												getExtensionHeight,
											);

											// IMPORTANT: If you change the code below, please also update the
											// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/placeholderPluginOptions/placeholderPluginOptions.ts
											const placeholderText =
												!viewMode &&
												_getPlaceholderText({
													featureFlags,
													isAIEnabledForContent,
												});

											const metaKey = isMac() ? '⌘' : 'Ctrl';

											return (
												<FullPageEditorComponentWrapper>
													<BlankPageQuickActionsProvider spaceKey={spaceKey}>
														<HeadingLinksStyleWrapper>
															<EditorWrapper {...wrapperProps}>
																{isLivePage && (
																	<>
																		<PublishModalMountPoint
																			onClickPublish={onClickPublish}
																			onClickPreview={onPreviewClicked}
																			retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
																			publishButtonDisabled={false}
																			title={title ?? draftTitle}
																			userViewingPreview={false}
																			isUnpublishedDraft={false}
																			contentType={contentType}
																			shouldShowWorldIcon={false}
																			shouldShowFooterButtons={false}
																			isConvertToPageDialog
																		/>
																		<PublishModalKeyboardShortcuts
																			contentId={contentId}
																			contentType={contentType}
																			isUnpublishedDraft={isUnpublishedDraft}
																			hasEditPermission={hasEditPermission}
																			flags={flags}
																			retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
																			saveDraft={saveDraft}
																		/>
																	</>
																)}
																{!shouldShowNewContentHistory &&
																	(editorExperiment('platform_editor_controls', 'variant1') ? (
																		<FullPageEditorBannerWrapper
																			isLivePage={isLivePage}
																			isEmbeddedConfluence={isEmbeddedConfluence}
																			viewMode={viewMode}
																			spaceKey={spaceKey}
																			editorFullPagePresetAPI={fullPageEditorPresetAPI}
																			triggerAnalyticsEvent={triggerAnalyticsEvent}
																			contentId={contentId}
																			version={version}
																			isShowingOfflineBanner={collabEditInternetLost}
																		/>
																	) : (
																		<>
																			{fg('aifc_create_enabled') && (
																				<SmartCreateFeedbackBannerLoader contentType="page" />
																			)}
																		</>
																	))}
																{showEmbeddedModernHeader &&
																	(fg('confluence_frontend_content_wrapper') ? (
																		<EditorObjectHeaderContextProvider
																			value={{
																				saveDraft,
																				navigateOnClose,
																				triggerAnalyticsEvent,
																				version,
																				editorActions,
																				title,
																				onClickOpenContentCreationPanel,
																				isContentCreationPanelOpen,
																				retrieveEditorTitleAndContent,
																				showRevertDialog,
																				onPreviewClicked,
																				hasEditPermission,
																				isPublishingContent,
																				userOffline,
																				forceDisabled,
																				editorTitle: title ?? draftTitle,
																				collabLimitReached,
																				collabEditConnected,
																				onClickPublish,
																				shouldRenderPreview,
																				shouldShowNewContentHistory,
																				isUnpublishedDraft,
																				validatePageAndOpenModal,
																				...editorActionsProps,
																				viewMode,
																			}}
																		>
																			<ExperienceStart name={OBJECT_HEADER_EXPERIENCE} />
																			<ContentTypesHeader contentId={contentId} />
																		</EditorObjectHeaderContextProvider>
																	) : (
																		<EditorActionBar
																			spaceKey={spaceKey}
																			editorActions={editorActions}
																			collabEditState={collabEditState}
																			title={title ?? draftTitle}
																			forceDisabled={forceDisabled}
																			userOffline={userOffline}
																			contentId={contentId}
																			contentType={contentType}
																			isContentCreationPanelOpen={isContentCreationPanelOpen}
																			isUnpublishedDraft={isUnpublishedDraft}
																			onClickPublish={onClickPublish}
																			onClickOpenContentCreationPanel={
																				onClickOpenContentCreationPanel
																			}
																			onPreviewClicked={onPreviewClicked}
																			saveDraft={saveDraft}
																			collabLimitReached={collabLimitReached}
																			showRevertDialog={showRevertDialog}
																			retrieveEditorTitleAndContent={retrieveEditorTitleAndContent}
																			isPublishingContent={isPublishingContent}
																			isSingleUserSession={isSingleUserSession}
																			navigateOnClose={navigateOnCloseStable}
																			triggerAnalyticsEvent={triggerAnalyticsEvent}
																			// Cheating with this prop for now for both since looks like this component might get cleaned up after object header rolls out
																			shouldRenderPreview={
																				shouldRenderPreview || shouldShowNewContentHistory
																			}
																			shouldShowRecentSpotlight={shouldShowRecentSpotlight}
																			checkIfConditionPreventingPublish={
																				checkIfConditionPreventingPublish
																			}
																			flags={flags}
																			createAnalyticsEvent={createAnalyticsEvent}
																			hasEditPermission={hasEditPermission}
																			editorDisabled={disabled}
																			isTitleFocused={isTitleFocused}
																			viewMode={viewMode}
																			contentClassificationMetadata={contentClassificationMetadata}
																			isLivePage={isLivePage}
																			requestToEditContent={requestToEditContent}
																			hasRequestedEditPermission={hasRequestedEditPermission}
																			ellipsisEditorActions={editorActionsProps}
																		/>
																	))}
																<EditorDisplayWrapper
																	data-testid="full-page-editor-style"
																	//TODO: Control isHidden prop
																	isHidden={shouldRenderPreview || shouldShowNewContentHistory}
																	isEditorActionBarRendered
																	isToolbarAifcEnabled={fg('aifc_create_enabled')}
																>
																	<EditorConnectionBannerWrapper
																		editorType="next"
																		triggerAnalyticsEvent={triggerAnalyticsEvent}
																		collabEditState={collabEditState}
																		collabEditInternetLost={collabEditInternetLost}
																		collabEditConnectionTimedOut={collabEditConnectionTimedOut}
																		willEditorDisable={!editorDisabled}
																		contentType={contentType}
																		contentId={contentId}
																		spaceId={spaceId}
																		fullPageEditorPresetAPI={fullPageEditorPresetAPI}
																		isViewMode={viewMode || false}
																		isLivePage={isLivePage}
																		isDesktop={isDesktop}
																	/>
																	{showHelpWidget && (
																		<OnboardingHelpContainer
																			contentId={contentId}
																			isContentCreationPanelOpen={isContentCreationPanelOpen}
																			onClickOpenContentCreationPanel={
																				onClickOpenContentCreationPanel
																			}
																			editorActions={editorActions}
																			isShowingOfflineBanner={collabEditInternetLost}
																			version={version}
																			spaceKey={spaceKey}
																			editorApi={fullPageEditorPresetAPI}
																			handleTitleChange={handleTitleChange}
																		/>
																	)}
																	{/* eslint-disable-next-line check-react-ssr-usage/no-react-ssr */}
																	{!process.env.REACT_SSR && (
																		<ConfluenceRecapModalLoadable
																			isUnpublishedDraft={isUnpublishedDraft}
																			onClickPublish={() =>
																				onPublish({
																					suppressNotifyWatchers: false,
																				})()
																			}
																		/>
																	)}
																	<PageLoadMark name="onBeforeEditor" />
																	<MacroExperienceStart
																		contentId={contentId}
																		mode={EDITOR}
																		name={VIEW_ALL_MACRO_EXPERIENCE}
																		timeout={ExperienceTimeout.MACRO_LOAD}
																	/>
																	{fg('confluence_footer_comment_styling_change') && (
																		<PageCommentsPubSubHandler
																			contentId={contentId}
																			contentType={contentType}
																		/>
																	)}
																	<EditorAnalyticsListener
																		contentId={contentId}
																		contentType={contentType}
																		source="pageAndBlogEditor" // TODO: add isTemplateEditorCheck
																		collabService={CollabServiceProperty.NCS}
																		onProseMirrorRendered={onProseMirrorRendered}
																		isGuest={isExternalCollaborator}
																	>
																		{
																			// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
																			!process.env.REACT_SSR &&
																				proseMirrorRenderedData &&
																				expVal(
																					'cc_comments_new_comments_data_manager',
																					'isEnabled',
																					false,
																				) && <CommentsDataBackgroundLoader contentId={contentId} />
																		}
																		<PrimaryToolbarContextProvider
																			value={{
																				forceDisabled,
																				userOffline,
																				collabLimitReached,
																				contentId,
																				contentType,
																				isContentCreationPanelOpen,
																				isUnpublishedDraft,
																				onClickPublish,
																				onClickOpenContentCreationPanel,
																				onPreviewClicked,
																				saveDraft,
																				spaceKey,
																				showRevertDialog,
																				retrieveEditorTitleAndContent,
																				isPublishingContent,
																				isSingleUserSession,
																				triggerAnalyticsEvent,
																				navigateOnClose: navigateOnCloseStable,
																				title,
																				editorActions,
																				disabled: disabled || collabLimitReached,
																				isTitleFocused,
																				collabEditState,
																				shouldRenderPreview,
																				shouldShowNewContentHistory,
																				shouldShowRecentSpotlight,
																				flags,
																				createAnalyticsEvent,
																				hasEditPermission,
																				ellipsisEditorActions: editorActionsProps,
																			}}
																		>
																			<ContentComponentsContextProvider
																				value={{
																					contentComponentsTitle: title ?? draftTitle,
																					contentComponentsDisabled:
																						disabled ||
																						collabLimitReached ||
																						!collabEditInitialized,
																					handleTitleChange,
																					breadcrumbsClickHandler: handleBreadcrumbsClick,
																					beforeUpdateRequest,
																					postUpdateRequest,
																				}}
																			>
																				<MultiMacroQueryContainer
																					contentId={contentId}
																					featureFlags={featureFlags}
																					readFragmentInterop={readFragmentInterop}
																					contentRendererMode={EDITOR}
																				>
																					<MentionsNotificationsWrapper
																						contentId={contentId}
																						flags={flags}
																						isLivePage={isLivePage}
																						isMentionsNotificationsEnabled={
																							isLivePage || isClassicMentionsNotificationsEnrolled
																						}
																						editorActions={editorActions}
																					>
																						<InlineCommentsProvider>
																							<ObjectCommentProvider>
																								<GuestMentionsNotificationsWrapper
																									dialogs={dialogs}
																									flags={flags}
																								>
																									<EditorLinkingFeatures
																										options={CARD_OPTIONS}
																										viewMode={viewMode}
																										isLivePage={isLivePage}
																									>
																										{(editorLinkingProps) => {
																											// TODO: fix me. ignored to get an unrelated refactor in: React Hooks must be called in a React function component or a custom React Hook function
																											/* eslint-disable react-hooks/rules-of-hooks */
																											const mainButtonsContext =
																												usePrimaryToolbarContext();
																											/* eslint-enable react-hooks/rules-of-hooks */

																											const primaryToolBarComponent =
																												!showEmbeddedModernHeader ? (
																													<EditorPrimaryToolbarComponents
																														{...mainButtonsContext}
																														allowInlineComments={
																															allowInlineComments
																														}
																														allowPageComments={allowPageComments}
																													/>
																												) : undefined;

																											return (
																												<>
																													{
																														<QuickReload
																															contentId={contentId}
																															contentType={contentType}
																															// The 'Date.now()' is expedient; see notes in quick-reload
																															// package on what a more correct lastPollTime value would be.
																															lastPollTime={Date.now()}
																															pageMode={
																																isLivePage
																																	? PageMode.LIVE
																																	: PageMode.EDITOR
																															}
																														/>
																													}
																													<ErrorBoundary
																														attribution={Attribution.EDITOR_CORE}
																													>
																														<FullPageComposableEditor
																															setEditorAPI={setEditorApi as any}
																															// Needed to create the referentiality plugin in the
																															// ComposableEditor
																															referentialityContext={
																																referentialityContext
																															}
																															editorPluginAIOptions={
																																editorPluginAIOptions
																															}
																															aiGlobalOptIn={aiGlobalOptIn}
																															editorPluginAIFeedbackHandlers={
																																editorPluginAIFeedbackHandlers
																															}
																															// === Config ===
																															{...extensionOptions}
																															extensionProviders={
																																extensionProvidersWithLCM
																															}
																															getExtensionHeight={
																																getExtensionHeight
																															}
																															allowConfluenceInlineComment={
																																allowInlineComments
																															}
																															allowFindReplace={
																																!collabEditInternetLost &&
																																findReplaceOptions
																															}
																															defaultValue={draftBody ?? undefined}
																															elementBrowser={elementBrowser}
																															media={mediaOptions}
																															mention={mentionProps}
																															placeholder={
																																placeholderText &&
																																intl.formatMessage(
																																	placeholderText,
																																	{ key: metaKey },
																																)
																															}
																															waitForMediaUpload
																															__rendererExtensionOptions={
																																extensionRendererViewOptions
																															}
																															// === State config ===
																															appearance={appearance ?? 'full-page'}
																															contentMode={
																																expValEquals(
																																	'cc_editor_ai_content_mode',
																																	'forceAll',
																																	true,
																																)
																																	? expValEquals(
																																			'confluence_content_mode_replace_dense_with_compact',
																																			'cohort',
																																			'test',
																																		)
																																		? 'compact'
																																		: 'dense'
																																	: editorContentMode
																															}
																															disabled={
																																isTitleFocused ||
																																editorDisabled ||
																																collabLimitReached
																															}
																															onChange={handleDocChange}
																															shouldFocus={
																																!!title && shouldFocusEditor
																															}
																															onEditorReady={onEditorReady}
																															// === Providers ===
																															activityProvider={activityProvider}
																															// In future, inlineCommentOptions should check if isTemplateEditor
																															annotationProviders={
																																allowInlineComments
																																	? annotationProviderOptions
																																	: undefined
																															}
																															autoformattingProvider={
																																autoformattingProvider
																															}
																															collabEditProvider={
																																collabEditProvider
																															}
																															collabEdit={collabEdit}
																															contextIdentifierProvider={
																																contextIdentifierProvider
																															}
																															emojiProvider={emojiProvider}
																															mentionProvider={mentionProvider}
																															searchProvider={searchProvider}
																															taskDecisionProvider={
																																taskDecisionProvider
																															}
																															syncedBlockProvider={
																																syncedBlockProvider
																															}
																															// === Components ===
																															contentComponents={_getContentComponents(
																																{
																																	contentId,
																																	debounceTitleChangeUpdates,
																																	focusEditor,
																																	onFocusChanged,
																																	userOffline,
																																	linkCreation:
																																		linkCreation === 'true',
																																	editorPluginAIFeedbackHandlers,
																																	isAdminHubAIEnabled,
																																	isAIEnabledForContent,
																																	hideSpinner,
																																	templateSuggestionsRef,
																																	templateButtonRef,
																																	editorActions,
																																	isTitleFocused,
																																	disabled,
																																	viewMode,
																																	contentClassificationMetadata,
																																	appearance,
																																	spaceKey,
																																	title,
																																	cancelDebouncedSaveDraft,
																																	showSpinner,
																																	onClickOpenContentCreationPanel,
																																	isUnpublishedDraft,
																																	featureFlags,
																																	fullPageEditorPresetAPI,
																																	showBlankPageQuickActions,
																																	isLivePage,
																																	version,
																																	isEditorEmpty,
																																	shouldRenderPreview,
																																	shouldShowNewContentHistory,
																																	isEditorAnnotationProviderManagersEnabled:
																																		fg(
																																			'confluence_frontend_editor_annotation_manager',
																																		),
																																	editorApi:
																																		fullPageEditorPresetAPI,
																																	contentType,
																																	onTitleChange,
																																	loadingPriority,
																																	isRovoButtonEnabled,
																																	editorSessionId,
																																	editorActionsProps,
																																	isSeamlessTransitionInProgress:
																																		fg(
																																			'confluence_frontend_transition_preloading_system',
																																		) &&
																																		isLiveDocSeamlessTransitioning,
																																	hasByLineContributors,
																																	hasByLineExtensions,
																																	hasReactions,
																																	hasPageComments,
																																	isEmbeddedConfluence,
																																	isTemplateBrowserEnabled,
																																},
																															)}
																															contextPanel={_getContextPanelComponent()}
																															featureFlags={
																																fullPageEditorFeatureFlags
																															}
																															performanceTracking={
																																performanceTrackingOptions
																															}
																															{...editorLinkingProps}
																															viewMode={viewMode ? 'view' : 'edit'}
																															__livePage={isLivePage}
																															flags={flags}
																															primaryToolbarComponents={
																																primaryToolBarComponent
																															}
																															hasEditPermission={hasEditPermission}
																															requestToEditContent={
																																requestToEditContent
																															}
																															hasRequestedEditPermission={
																																hasRequestedEditPermission
																															}
																															getHeadingLinkPath={
																																getHeadingLinkPath
																															}
																															contentId={contentId}
																															spaceId={spaceId}
																															spaceKey={spaceKey}
																															isAIEnabledForContent={
																																isAIEnabledForContent
																															}
																															showEmbeddedModernHeader={
																																showEmbeddedModernHeader
																															}
																															isCompanyHub={isCompanyHub}
																														/>
																													</ErrorBoundary>

																													<EditorCommentShortcutListener
																														contentId={contentId}
																														viewMode={viewMode}
																														editorApi={fullPageEditorPresetAPI}
																													/>
																												</>
																											);
																										}}
																									</EditorLinkingFeatures>
																								</GuestMentionsNotificationsWrapper>
																							</ObjectCommentProvider>
																						</InlineCommentsProvider>
																					</MentionsNotificationsWrapper>
																				</MultiMacroQueryContainer>
																			</ContentComponentsContextProvider>
																		</PrimaryToolbarContextProvider>
																	</EditorAnalyticsListener>
																	<Spinner
																		shouldShowSpinner={
																			shouldShowSpinner ||
																			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
																			(fg('confluence_frontend_transition_preloading_system') &&
																				isLiveDocSeamlessTransitioning)
																		}
																		isOverlayTransparent={
																			!shouldShowSpinner &&
																			fg('confluence_frontend_transition_preloading_system') &&
																			isLiveDocSeamlessTransitioning
																		}
																	/>
																</EditorDisplayWrapper>
																<AIOptInModal />

																<EditorRevertDialog
																	contentId={contentId}
																	spaceKey={spaceKey}
																	isUnpublishedDraft={isUnpublishedDraft}
																	cancelDebouncedSaveDraft={cancelDebouncedSaveDraft}
																/>
																<OnboardingEditorModalPrompt
																	onTitleChange={onTitleChange}
																	handlePublish={() =>
																		onPublish({
																			suppressNotifyWatchers: false,
																		})()
																	}
																/>
																<ConditionalPublish
																	contentId={contentId}
																	spaceKey={spaceKey}
																	setCheckIfConditionPreventingPublish={assignConditionalFunction}
																/>
																<PageLoadWrapper
																	disabled={disabled}
																	contentId={contentId}
																	isCreate={isUnpublishedDraft}
																	proseMirrorRenderedData={proseMirrorRenderedData}
																	editSource={editSource}
																	extractedCollabEditProvider={extractedCollabEditProvider}
																	isLivePage={isLivePage}
																	customPerfData={customPerfData}
																	draftBody={draftBody}
																/>
																{/* shouldShowNewContentHistory won't ever be true unless our experiment is enabled in the entry point which controls it */}
																{shouldShowNewContentHistory ? (
																	<NewContentHistory
																		contentId={contentId}
																		spaceKey={spaceKey}
																		contentType={contentType}
																	/>
																) : null}
																{shouldRenderPreview ? (
																	<RendererExtensionContext.Provider
																		value={rendererExtensionOptions}
																	>
																		<PreviewPageComponent
																			contentId={contentId}
																			spaceKey={spaceKey}
																			stagedContentId={stagedContentId}
																			stagedSpaceKey={stagedSpaceKey}
																			contentType={contentType as ContentTypeType}
																			contentAppearance={contentAppearance}
																			title={title}
																			editorActions={editorActions}
																			showTemplateVariablesInPreview
																			changeEditorMode={() => setShouldRenderPreview(false)}
																			Renderer={PageContentRenderer}
																			isPageContentRenderer
																			editorType={EditorTypes.NCS}
																			previewAsBlog={previewAsBlog}
																			contentClassificationMetadata={contentClassificationMetadata}
																		/>
																	</RendererExtensionContext.Provider>
																) : null}
																{contentType === 'page' && isLivePagesFeatureEnabled && (
																	<ConvertToLivePageFlag />
																)}
																{expVal(
																	'confluence_modernized_move_dialog',
																	'isEnabled',
																	false,
																) && <MovePageSuccessFlag />}

																{!isEmbeddedConfluence && (
																	<RovoBridgeCommandListener
																		contentId={contentId}
																		contentLocation="global"
																		editorAPI={fullPageEditorPresetAPI}
																		setEditorDisabled={setForceDisabled}
																		mediaProvider={mediaOptions?.provider}
																	/>
																)}
															</EditorWrapper>
															{fullPageEditorPresetAPI &&
																fg('cc_improve_writing_on_paste_gate') && (
																	<ImproveWritingFlag editorAPI={fullPageEditorPresetAPI} />
																)}
														</HeadingLinksStyleWrapper>
														{_getEditorContextConnectionsPanel({
															referentialityContext,
															connectionsPanelActiveNodeLocalId,
															setConnectionsPanelActiveNodeLocalId,
															closeConnectionsPanel,
														})}
														{
															// This FF controls if the right rail config panel is available in when editing a page
															isSmartButtonAllowed
																? _getEditorContextSmartButtonConfigPanel({
																		smartButtonActiveNodeLocalId,
																		closeSmartButtonConfigPanel,
																		fullPageEditorPresetAPI,
																	})
																: null
														}
														<EditorContextCustomSitesConfigPanelEntryPoint
															{...{
																localId: activeNodeLocalId || '',
																closeSelf: closeConfigPanel,
																editorApi: fullPageEditorPresetAPI || undefined,
																contentId,
																upsellModalSource,
																setUpsellModalSource,
															}}
														/>
													</BlankPageQuickActionsProvider>
												</FullPageEditorComponentWrapper>
											);
										}}
									</ContentAppearanceContextConsumer>
								</ContentAppearanceContextProvider>
							)}
						</EditorContextContentCreationPanelWrapper>
					</ClassificationModalContextProvider>
				</EditorContextPanelContextProvider>
			</SaveIndicatorContextProvider>
			<DocumentEditPageTitle
				contentType={contentType as ContentTypeType}
				isCreatingNew={isUnpublishedDraft}
				spaceKey={spaceKey}
				title={title}
				isLivePage={isLivePage}
			/>
			<EditorNavigationLockout
				contentId={contentId}
				isPublishingContent={isPublishingContent}
				isFirstParticipant={isFirstParticipant}
				editorPresetAPI={fullPageEditorPresetAPI}
			/>
			{printLockout}
		</UFOLabel>
	);
};

export const FullPageEditorComponent = withRovoSystemSpaceNotFound(
	withDocChangeContext(
		withEditorContext(withEditorActions(withFlags(withDialogs(FullPageEditorComponentImpl)))),
	),
);
